import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatIDR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    Pencil,
    Trash2,
    Plus,
    Upload,
    Download,
    FileSpreadsheet,
    QrCode,
    CalendarClock,
    Building2,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
} from "lucide-react";
import * as XLSX from "xlsx";
import { FAS_CATEGORIES } from "../constants/fasilitas";

const emptyForm = {
    name: "",
    code: "",
    unit_name: "",
    first_use_date: "",
    purchase_price: 0,
    current_value: 0,
    monthly_depreciation: 0,
    remaining_life: 0,
    tangible: true,
    useful_life_months: 0,
    depreciation_rate: 0,
    asset_type: "",
    brand: "",
    serial_number: "",
    notes: "",
    vendor_name: "",
    vendor_contact: "",
    location: "",
    distribute_to: "",
};

const NUM_FIELDS = [
    "purchase_price",
    "current_value",
    "monthly_depreciation",
    "remaining_life",
    "useful_life_months",
    "depreciation_rate",
];

// Peta header Excel (huruf kecil) -> field
const HEADER_MAP = {
    "nama aset": "name",
    "kode aset": "code",
    "nama unit": "unit_name",
    "tanggal pemakaian pertama asset": "first_use_date",
    "tanggal pemakaian pertama": "first_use_date",
    "harga perolehan": "purchase_price",
    "nilai aset saat ini": "current_value",
    "nilai penyusutan(per-bulan)": "monthly_depreciation",
    "nilai penyusutan (per-bulan)": "monthly_depreciation",
    "nilai penyusutan": "monthly_depreciation",
    "sisa umur": "remaining_life",
    "apakah aset berwujud?": "tangible",
    "apakah aset berwujud": "tangible",
    "masa manfaat aset": "useful_life_months",
    "tarif penyusutan": "depreciation_rate",
    "nama tipe aset": "asset_type",
    "merk": "brand",
    "merk aset": "brand",
    "serial number": "serial_number",
    "no seri": "serial_number",
    "nomor seri": "serial_number",
    "keterangan": "notes",
    "keterangan lainnya": "notes",
    "produsen": "vendor_name",
    "kontak produsen": "vendor_contact",
    "lokasi aset": "location",
    "distribusikan ke": "distribute_to",
};

const EXPORT_HEADERS = [
    "Nama Aset",
    "Kode Aset",
    "Nama Unit",
    "Tanggal Pemakaian Pertama Asset",
    "Harga Perolehan",
    "Nilai Aset Saat ini",
    "Nilai Penyusutan(per-bulan)",
    "Sisa Umur",
    "Apakah Aset Berwujud?",
    "Masa Manfaat Aset",
    "Tarif Penyusutan",
    "Nama Tipe Aset",
    "Merk",
    "Serial Number",
    "Produsen",
    "Kontak Produsen",
    "Lokasi Aset",
    "Distribusikan ke",
    "Keterangan",
];

function parseNum(v) {
    if (typeof v === "number") return v;
    if (v == null || v === "") return 0;
    const s = String(v).trim().replace(/\./g, "").replace(",", ".");
    return Number(s) || 0;
}

function parseDate(v) {
    if (!v) return "";
    if (v instanceof Date) {
        const off = new Date(v.getTime() - v.getTimezoneOffset() * 60000);
        return off.toISOString().slice(0, 10);
    }
    return String(v).slice(0, 10);
}

function parseBool(v) {
    if (typeof v === "boolean") return v;
    const s = String(v).trim().toLowerCase();
    if (["false", "tidak", "no", "0"].includes(s)) return false;
    return true;
}

function fmtDate(s) {
    if (!s) return "-";
    try {
        return new Date(s).toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return s;
    }
}

export default function Assets() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [selected, setSelected] = useState(new Set());
    const [sort, setSort] = useState({ key: "name", dir: "asc" });
    const [page, setPage] = useState(1);
    const perPage = 20;
    const fileRef = useRef(null);

    async function load() {
        setLoading(true);
        try {
            const res = await api.get("/assets");
            setItems(res.data);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    function openAdd() {
        setEditing(null);
        setForm(emptyForm);
        setDialogOpen(true);
    }

    function openEdit(a) {
        setEditing(a);
        setForm({ ...emptyForm, ...a });
        setDialogOpen(true);
    }

    async function save() {
        if (!form.name.trim() || !form.code.trim()) {
            toast.error("Nama dan Kode Aset wajib diisi");
            return;
        }
        const payload = { ...form };
        for (const k of NUM_FIELDS) payload[k] = parseNum(payload[k]);
        try {
            if (editing) {
                await api.put(`/assets/${editing.id}`, payload);
                toast.success("Aset diperbarui");
            } else {
                await api.post("/assets", payload);
                toast.success("Aset ditambahkan");
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function remove(a) {
        if (!window.confirm(`Hapus aset "${a.name}" (${a.code})?`)) return;
        try {
            await api.delete(`/assets/${a.id}`);
            toast.success("Aset dihapus");
            setSelected((prev) => {
                const n = new Set(prev);
                n.delete(a.id);
                return n;
            });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function depreciate() {
        if (
            !window.confirm(
                "Hitung ulang Nilai Aset Saat Ini & Sisa Umur semua aset berdasarkan Tanggal Pemakaian Pertama sampai hari ini?"
            )
        )
            return;
        try {
            const res = await api.post("/assets/depreciate");
            toast.success(
                `Penyusutan dihitung: ${res.data.updated} aset diperbarui${
                    res.data.skipped ? `, ${res.data.skipped} dilewati (data belum lengkap)` : ""
                }`
            );
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function applyToUnitCost() {
        if (
            !window.confirm(
                "Salin total penyusutan bulanan aset per kategori ruangan ke bagian Penyusutan di Unit Cost Fasilitas?"
            )
        )
            return;
        try {
            const res = await api.post("/assets/apply-to-unit-cost");
            toast.success(
                `Penyusutan tersalin ke ${res.data.categories_updated} kategori ruangan Unit Cost Fasilitas`
            );
            if (res.data.unmapped_categories?.length) {
                toast.info(
                    `Kategori tidak dikenali (dilewati): ${res.data.unmapped_categories.join(", ")}`
                );
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function handleImport(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: "array", cellDates: true });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
            const rows = raw
                .map((r) => {
                    const o = { ...emptyForm };
                    for (const [h, v] of Object.entries(r)) {
                        const key = HEADER_MAP[String(h).trim().toLowerCase()];
                        if (!key) continue;
                        if (NUM_FIELDS.includes(key)) o[key] = parseNum(v);
                        else if (key === "first_use_date") o[key] = parseDate(v);
                        else if (key === "tangible") o[key] = parseBool(v);
                        else o[key] = String(v ?? "").trim();
                    }
                    o.code = String(o.code || "").trim();
                    return o;
                })
                .filter((o) => o.code && o.name);
            if (!rows.length) {
                toast.error("Tidak ada baris valid. Pastikan kolom 'Nama Aset' & 'Kode Aset' terisi.");
                return;
            }
            const res = await api.post("/assets/bulk-import", { items: rows });
            toast.success(
                `Impor selesai: ${res.data.inserted} baru, ${res.data.updated} diperbarui, ${res.data.skipped} dilewati`
            );
            load();
        } catch (err) {
            toast.error(`Gagal impor: ${formatApiError(err)}`);
        }
    }

    function exportExcel() {
        const rows = items.map((a) => ({
            "Nama Aset": a.name,
            "Kode Aset": a.code,
            "Nama Unit": a.unit_name || "",
            "Tanggal Pemakaian Pertama Asset": a.first_use_date || "",
            "Harga Perolehan": a.purchase_price || 0,
            "Nilai Aset Saat ini": a.current_value || 0,
            "Nilai Penyusutan(per-bulan)": a.monthly_depreciation || 0,
            "Sisa Umur": a.remaining_life || 0,
            "Apakah Aset Berwujud?": a.tangible ? "true" : "false",
            "Masa Manfaat Aset": a.useful_life_months || 0,
            "Tarif Penyusutan": a.depreciation_rate || 0,
            "Nama Tipe Aset": a.asset_type || "",
            "Merk": a.brand || "",
            "Serial Number": a.serial_number || "",
            "Produsen": a.vendor_name || "",
            "Kontak Produsen": a.vendor_contact || "",
            "Lokasi Aset": a.location || "",
            "Distribusikan ke": a.distribute_to || "",
            "Keterangan": a.notes || "",
        }));
        const ws = XLSX.utils.json_to_sheet(rows, { header: EXPORT_HEADERS });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "master fixed aset");
        XLSX.writeFile(wb, `inventaris-aset-${new Date().toISOString().slice(0, 10)}.xlsx`);
    }

    function downloadTemplate() {
        const example = {
            "Nama Aset": "Electro Therapy",
            "Kode Aset": "131000",
            "Nama Unit": "Fisioterapi",
            "Tanggal Pemakaian Pertama Asset": "2025-01-01",
            "Harga Perolehan": 80000000,
            "Nilai Aset Saat ini": "",
            "Nilai Penyusutan(per-bulan)": "",
            "Sisa Umur": "",
            "Apakah Aset Berwujud?": "true",
            "Masa Manfaat Aset": 60,
            "Tarif Penyusutan": "",
            "Nama Tipe Aset": "Alat Kedokteran dan Kesehatan",
            "Merk": "GE Healthcare",
            "Serial Number": "SN-2025-0001",
            "Produsen": "PT. Buana Intiprima Medika",
            "Kontak Produsen": "08123456789",
            "Lokasi Aset": "Kamar OK",
            "Distribusikan ke": "Ruang OK",
            "Keterangan": "Kondisi baik",
        };
        const ws = XLSX.utils.json_to_sheet([example], { header: EXPORT_HEADERS });
        const notes = [
            [],
            ["Petunjuk:"],
            ["- Kode Aset unik. Baris dengan kode sama akan memperbarui data (upsert)."],
            ["- Kosongkan Nilai Penyusutan / Nilai Saat Ini / Sisa Umur / Tarif agar dihitung otomatis dari Harga Perolehan & Masa Manfaat (bulan)."],
            [`- Distribusikan ke (kategori ruangan Unit Cost Fasilitas): ${FAS_CATEGORIES.join(", ")}`],
        ];
        XLSX.utils.sheet_add_aoa(ws, notes, { origin: -1 });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "master fixed aset");
        XLSX.writeFile(wb, "template-inventaris-aset.xlsx");
    }

    function toggleSelect(id) {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    }

    function printLabels() {
        if (selected.size === 0) {
            toast.error("Pilih minimal 1 aset untuk cetak label");
            return;
        }
        sessionStorage.setItem("asset_label_ids", JSON.stringify([...selected]));
        navigate("/assets/print");
    }

    function toggleSort(key) {
        setSort((s) =>
            s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
        );
    }

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        let list = items.filter(
            (a) =>
                a.name.toLowerCase().includes(q) ||
                a.code.toLowerCase().includes(q) ||
                (a.unit_name || "").toLowerCase().includes(q) ||
                (a.location || "").toLowerCase().includes(q) ||
                (a.distribute_to || "").toLowerCase().includes(q)
        );
        const { key, dir } = sort;
        list = [...list].sort((a, b) => {
            const va = a[key] ?? "";
            const vb = b[key] ?? "";
            const cmp =
                typeof va === "number" || typeof vb === "number"
                    ? (Number(va) || 0) - (Number(vb) || 0)
                    : String(va).localeCompare(String(vb), "id");
            return dir === "asc" ? cmp : -cmp;
        });
        return list;
    }, [items, search, sort]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const curPage = Math.min(page, totalPages);
    const paged = filtered.slice((curPage - 1) * perPage, curPage * perPage);
    const allPageSelected = paged.length > 0 && paged.every((a) => selected.has(a.id));

    function togglePageSelect() {
        setSelected((prev) => {
            const n = new Set(prev);
            if (allPageSelected) paged.forEach((a) => n.delete(a.id));
            else paged.forEach((a) => n.add(a.id));
            return n;
        });
    }

    const SortIcon = ({ col }) =>
        sort.key !== col ? (
            <ArrowUpDown size={12} className="inline ml-1 opacity-50" />
        ) : sort.dir === "asc" ? (
            <ArrowUp size={12} className="inline ml-1" />
        ) : (
            <ArrowDown size={12} className="inline ml-1" />
        );

    return (
        <div className="space-y-6" data-testid="assets-page">
            <div>
                <div className="label-uppercase mb-2">Master Data</div>
                <h1 className="font-heading text-4xl tracking-tight font-semibold">
                    Inventaris Aset
                </h1>
                <p className="text-[#4A5E57] mt-2 max-w-3xl">
                    Kelola aset tetap rumah sakit: tambah satu per satu atau impor Excel,
                    hitung penyusutan otomatis, salin penyusutan per ruangan ke Unit Cost
                    Fasilitas, dan cetak label QR (Tom &amp; Jerry No. 121).
                </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Input
                    placeholder="Cari nama, kode, unit, lokasi..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                    data-testid="assets-search"
                    className="max-w-xs"
                />
                <Button
                    onClick={openAdd}
                    data-testid="assets-add-btn"
                    className="bg-[#245B4B] hover:bg-[#1A4236] text-white"
                >
                    <Plus size={16} className="mr-1" /> Tambah Aset
                </Button>
                <Button
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    data-testid="assets-import-btn"
                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                >
                    <Upload size={16} className="mr-1" /> Impor Excel
                </Button>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleImport}
                    data-testid="assets-import-input"
                />
                <Button
                    variant="outline"
                    onClick={exportExcel}
                    data-testid="assets-export-btn"
                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                >
                    <Download size={16} className="mr-1" /> Export
                </Button>
                <Button
                    variant="outline"
                    onClick={downloadTemplate}
                    data-testid="assets-template-btn"
                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                >
                    <FileSpreadsheet size={16} className="mr-1" /> Template
                </Button>
                <Button
                    variant="outline"
                    onClick={depreciate}
                    data-testid="assets-depreciate-btn"
                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                >
                    <CalendarClock size={16} className="mr-1" /> Hitung Penyusutan
                </Button>
                <Button
                    variant="outline"
                    onClick={applyToUnitCost}
                    data-testid="assets-apply-uc-btn"
                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                >
                    <Building2 size={16} className="mr-1" /> Salin ke Unit Cost Fasilitas
                </Button>
                <Button
                    onClick={printLabels}
                    data-testid="assets-print-labels-btn"
                    disabled={selected.size === 0}
                    className="bg-[#1A362D] hover:bg-[#12261F] text-white disabled:opacity-50"
                >
                    <QrCode size={16} className="mr-1" /> Cetak Label ({selected.size})
                </Button>
            </div>

            <div className="surface-card overflow-hidden">
                {loading ? (
                    <div className="p-8 text-[#4A5E57]">Memuat...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[#E9EFEA]">
                                    <TableHead className="w-10">
                                        <Checkbox
                                            checked={allPageSelected}
                                            onCheckedChange={togglePageSelect}
                                            data-testid="assets-select-all"
                                        />
                                    </TableHead>
                                    <TableHead
                                        className="cursor-pointer select-none"
                                        onClick={() => toggleSort("name")}
                                        data-testid="assets-sort-name"
                                    >
                                        Nama Aset <SortIcon col="name" />
                                    </TableHead>
                                    <TableHead
                                        className="cursor-pointer select-none"
                                        onClick={() => toggleSort("unit_name")}
                                        data-testid="assets-sort-unit"
                                    >
                                        Nama Unit <SortIcon col="unit_name" />
                                    </TableHead>
                                    <TableHead
                                        className="cursor-pointer select-none"
                                        onClick={() => toggleSort("first_use_date")}
                                    >
                                        Tgl Pakai <SortIcon col="first_use_date" />
                                    </TableHead>
                                    <TableHead
                                        className="text-right cursor-pointer select-none"
                                        onClick={() => toggleSort("purchase_price")}
                                    >
                                        Harga Perolehan <SortIcon col="purchase_price" />
                                    </TableHead>
                                    <TableHead
                                        className="text-right cursor-pointer select-none"
                                        onClick={() => toggleSort("monthly_depreciation")}
                                    >
                                        Penyusutan/bln <SortIcon col="monthly_depreciation" />
                                    </TableHead>
                                    <TableHead
                                        className="text-right cursor-pointer select-none"
                                        onClick={() => toggleSort("current_value")}
                                    >
                                        Nilai Saat Ini <SortIcon col="current_value" />
                                    </TableHead>
                                    <TableHead className="text-right">Sisa Umur</TableHead>
                                    <TableHead>Lokasi</TableHead>
                                    <TableHead>Distribusikan ke</TableHead>
                                    <TableHead className="w-24 text-right">Aksi</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paged.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={11} className="text-center py-8 text-[#6A9C89]">
                                            Belum ada aset. Tambahkan satu per satu atau impor Excel.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paged.map((a) => (
                                        <TableRow key={a.id} data-testid={`asset-row-${a.code}`}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selected.has(a.id)}
                                                    onCheckedChange={() => toggleSelect(a.id)}
                                                    data-testid={`asset-check-${a.code}`}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-medium">{a.name}</div>
                                                <div className="text-xs text-[#6A9C89]">{a.code}</div>
                                            </TableCell>
                                            <TableCell
                                                className="text-sm"
                                                data-testid={`asset-unit-${a.code}`}
                                            >
                                                {a.unit_name || "-"}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap text-sm">
                                                {fmtDate(a.first_use_date)}
                                            </TableCell>
                                            <TableCell className="text-right whitespace-nowrap">
                                                {formatIDR(a.purchase_price)}
                                            </TableCell>
                                            <TableCell className="text-right whitespace-nowrap">
                                                {formatIDR(a.monthly_depreciation)}
                                            </TableCell>
                                            <TableCell className="text-right whitespace-nowrap font-medium">
                                                {formatIDR(a.current_value)}
                                            </TableCell>
                                            <TableCell className="text-right whitespace-nowrap">
                                                {a.remaining_life || 0} bln
                                            </TableCell>
                                            <TableCell className="text-sm">{a.location || "-"}</TableCell>
                                            <TableCell className="text-sm">
                                                {a.distribute_to || "-"}
                                            </TableCell>
                                            <TableCell className="text-right whitespace-nowrap">
                                                <button
                                                    onClick={() => openEdit(a)}
                                                    data-testid={`asset-edit-${a.code}`}
                                                    className="p-1.5 rounded hover:bg-[#E9EFEA] text-[#245B4B]"
                                                    title="Edit"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    onClick={() => remove(a)}
                                                    data-testid={`asset-delete-${a.code}`}
                                                    className="p-1.5 rounded hover:bg-[#F5E6E1] text-[#B3432B]"
                                                    title="Hapus"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
                <div className="flex items-center justify-between px-4 py-3 border-t border-[#D1DCD8] text-sm">
                    <div className="text-[#4A5E57]" data-testid="assets-count">
                        {filtered.length} aset
                        {selected.size > 0 ? ` • ${selected.size} dipilih` : ""}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={curPage <= 1}
                            onClick={() => setPage(curPage - 1)}
                            data-testid="assets-prev-page"
                            className="border-[#D1DCD8]"
                        >
                            Sebelumnya
                        </Button>
                        <span className="text-[#4A5E57]">
                            {curPage} / {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={curPage >= totalPages}
                            onClick={() => setPage(curPage + 1)}
                            data-testid="assets-next-page"
                            className="border-[#D1DCD8]"
                        >
                            Berikutnya
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Aset" : "Tambah Aset"}</DialogTitle>
                        <DialogDescription>
                            Kosongkan nilai turunan (penyusutan, tarif, nilai saat ini, sisa umur)
                            agar dihitung otomatis dari Harga Perolehan &amp; Masa Manfaat.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Nama Aset *</Label>
                            <Input
                                value={form.name}
                                data-testid="asset-form-name"
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Kode Aset *</Label>
                            <Input
                                value={form.code}
                                data-testid="asset-form-code"
                                onChange={(e) => setForm({ ...form, code: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Nama Unit</Label>
                            <Input
                                value={form.unit_name}
                                onChange={(e) => setForm({ ...form, unit_name: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Tanggal Pemakaian Pertama</Label>
                            <Input
                                type="date"
                                value={form.first_use_date || ""}
                                data-testid="asset-form-date"
                                onChange={(e) => setForm({ ...form, first_use_date: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Harga Perolehan (Rp)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.purchase_price}
                                data-testid="asset-form-price"
                                onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Masa Manfaat (bulan)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.useful_life_months}
                                data-testid="asset-form-life"
                                onChange={(e) =>
                                    setForm({ ...form, useful_life_months: e.target.value })
                                }
                            />
                        </div>
                        <div>
                            <Label>Penyusutan / bulan (Rp)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.monthly_depreciation}
                                onChange={(e) =>
                                    setForm({ ...form, monthly_depreciation: e.target.value })
                                }
                            />
                            <div className="text-[11px] text-[#6A9C89] mt-0.5">
                                Kosongkan (0) agar dihitung otomatis: Harga ÷ Masa Manfaat
                            </div>
                        </div>
                        <div>
                            <Label>Tarif Penyusutan (%/tahun)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.depreciation_rate}
                                onChange={(e) =>
                                    setForm({ ...form, depreciation_rate: e.target.value })
                                }
                            />
                            <div className="text-[11px] text-[#6A9C89] mt-0.5">
                                Kosongkan (0) agar dihitung otomatis
                            </div>
                        </div>
                        <div>
                            <Label>Nilai Aset Saat Ini (Rp)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.current_value}
                                onChange={(e) => setForm({ ...form, current_value: e.target.value })}
                            />
                            <div className="text-[11px] text-[#6A9C89] mt-0.5">
                                Kosongkan (0) agar dihitung otomatis dari tanggal pemakaian
                            </div>
                        </div>
                        <div>
                            <Label>Sisa Umur (bulan)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.remaining_life}
                                onChange={(e) => setForm({ ...form, remaining_life: e.target.value })}
                            />
                            <div className="text-[11px] text-[#6A9C89] mt-0.5">
                                Kosongkan (0) agar dihitung otomatis
                            </div>
                        </div>
                        <div>
                            <Label>Nama Tipe Aset</Label>
                            <Input
                                value={form.asset_type}
                                onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Merk Aset</Label>
                            <Input
                                value={form.brand}
                                data-testid="asset-form-brand"
                                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Serial Number</Label>
                            <Input
                                value={form.serial_number}
                                data-testid="asset-form-serial"
                                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                            />
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                            <Checkbox
                                checked={!!form.tangible}
                                onCheckedChange={(v) => setForm({ ...form, tangible: !!v })}
                                data-testid="asset-form-tangible"
                            />
                            <Label className="cursor-pointer" onClick={() => setForm({ ...form, tangible: !form.tangible })}>
                                Aset Berwujud
                            </Label>
                        </div>
                        <div>
                            <Label>Produsen / Vendor</Label>
                            <Input
                                value={form.vendor_name}
                                data-testid="asset-form-vendor"
                                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Kontak Vendor</Label>
                            <Input
                                value={form.vendor_contact}
                                onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Lokasi Aset</Label>
                            <Input
                                value={form.location}
                                data-testid="asset-form-location"
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Distribusikan ke (Ruangan)</Label>
                            <Select
                                value={form.distribute_to || "none"}
                                onValueChange={(v) =>
                                    setForm({ ...form, distribute_to: v === "none" ? "" : v })
                                }
                            >
                                <SelectTrigger data-testid="asset-form-distribute">
                                    <SelectValue placeholder="Pilih kategori ruangan" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">- Tidak didistribusikan -</SelectItem>
                                    {FAS_CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c} data-testid={`asset-dist-opt-${c}`}>
                                            {c}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="text-[11px] text-[#6A9C89] mt-0.5">
                                Mengacu pada kategori ruangan Unit Cost Fasilitas
                            </div>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Keterangan Lainnya</Label>
                            <Textarea
                                value={form.notes}
                                data-testid="asset-form-notes"
                                rows={2}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            className="border-[#D1DCD8]"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={save}
                            data-testid="asset-form-save"
                            className="bg-[#245B4B] hover:bg-[#1A4236] text-white"
                        >
                            {editing ? "Simpan Perubahan" : "Tambah"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
